import { Configuration, Custom, LegalTextData, Tutorial } from '@core/models';
import { checkoutTutorialSteps } from '../tutorial/checkout/steps';
import {
  seatSelectionDesktopTutorialSteps,
  seatSelectionMobileTutorialSteps,
} from '../tutorial/seat-selection/steps';

const custom: Custom = {
  paymentPage: {
    captureAddress: false,
    capturePhone: true,
  },
};

const seatSelectionDesktopTutorial: Tutorial = {
  sectionAvailabilityIds: ['15685', '15737', '15763', '15679'],
  availableSeatsQuantity: 240,
  selectedSeatsQuantity: 30,
  allowClose: false,
  doneBtnText: 'End Tutorial',
  nextBtnText: 'Next',
  popoverClass: 'driverjs-theme',
  prevBtnText: 'Back',
  steps: seatSelectionDesktopTutorialSteps,
};

const seatSelectionMobileTutorial: Tutorial = {
  ...seatSelectionDesktopTutorial,
  steps: seatSelectionMobileTutorialSteps,
};

const checkoutTutorial: Tutorial = {
  ...seatSelectionDesktopTutorial,
  steps: checkoutTutorialSteps,
};

export const ChicagoCubsConfig: Configuration = {
  id: 'chicagocubs',
  clientName: 'Chicago Cubs',
  appName: 'Chicago Cubs',
  city: 'Chicago',
  favicon: '/assets/img/favicon.png',
  logo: '/assets/img/logo.svg',
  phone: '773-404-4242',
  rainout: true,
  upsells: true,
  adas: true,
  email: 'cubsgrouptickets@cubs.com',
  paymentMethod: 'braintree',
  clientWebUrl: 'http://www.cubs.com/',
  clientWebText: 'www.cubs.com.',
  clientBallParkUrl:
    'https://www.mlb.com/cubs/apps/ballpark?partnerId=redirect-chc-ballparkapp',
  clientBallParkText: 'Cubs.com/BallparkApp',
  mlbShopUrl:
    'https://www.mlbshop.com/chicago-cubs/t-36228622+z-808474-1125435766',
  merchandiseWebUrl: '',
  rainoutInsuranceUrl:
    'https://www.mlb.com/cubs/tickets/group-tickets/rainout-insurance',
  termsAndConditionsUrl:
    'https://www.mlb.com/cubs/tickets/terms-and-conditions',
  termsOfUseUrl: 'https://www.mlb.com/official-information/terms-of-use',
  analytics: '',
  theme: {
    'default-color': '0 90 156',
    'main-color': '0 90 156',
    'secondary-color': '25 149 255',
    'tertiary-color': '242 246 250',
    'accent-color': '189 48 57',
    red: '225 56 63',
    success: '86 163 67',
    'success-off-white': '236 246 232',
  },
  dvmData: {
    venueId: 'nam-us-00096-chicagocubs',
    mapId: 'main_venuemap',
    miniMapId: 'blockmap_split_minimap',
    miniMapEnabled: true,
    gaSectorsIdArray: ['15404'],
    gaAdaIdArray: ['15427'],
    sectionOffsets: {
      '15404': [-80, -360],
    },
    limitedZoom: false,
  },
  dvmStyles: {
    'seat-available-none-fill': '25 149 255',
    'seat-available-none-stroke': '0 90 156',
    'seat-available-disabled-fill': '25 149 255',
    'seat-available-disabled-stroke': '0 90 156',
    'seat-unavailable-none': '214 214 214',
    'seat-available-pending': '230 145 56',
    'section-available-normal-fill': '25 149 255',
    'section-available-normal-stroke': '0 90 156',
    'section-available-normal-group1-fill': '255 77 77',
    'section-available-normal-group1-stroke': '255 77 77',
    'section-available-hover-fill': '0 90 156',
    'section-available-hover-stroke': '255 255 255',
    'section-selected-normal-fill': '225 56 63',
    'section-selected-normal-stroke': '225 56 63',
    'section-selected-hover-stroke': '255 255 255',
    'section-nh-available-normal-fill': '229 154 65',
    'section-nh-available-normal-stroke': '229 154 65',
    'section-nh-available-hover-fill': '229 154 65',
    'section-nh-available-hover-stroke': '229 154 65',
    'section-nh-unavailable-normal-fill': 'transparent',
    'section-nh-unavailable-normal-stroke': 'transparent',
    'section-nh-unavailable-hover-fill': 'transparent',
    'section-nh-unavailable-hover-stroke': 'transparent',
  },
  custom,
  tutorial: {
    seatSelection: {
      desktop: seatSelectionDesktopTutorial,
      mobile: seatSelectionMobileTutorial,
    },
    checkout: { desktop: checkoutTutorial, mobile: checkoutTutorial },
  },
  cartSummaryText:
    'All orders are subject to a 12% City and County Amusement Tax',
  summaryPage: {
    image1: '/assets/img/cubsspring/cubs_logo.png',
    image2: '',
  },
  get legalText(): LegalTextData {
    return {
      links: {
        clubTermsLink: {
          href: this.termsAndConditionsUrl,
          text: 'Terms & Conditions',
          target: '_blank',
        },
        clubWebLink: {
          href: this.clientWebUrl,
          text: this.clientWebText,
          target: '_blank',
        },
        mlbTermsOfUseLink: {
          href: this.termsOfUseUrl,
          text: 'MLB Terms of Use',
          target: '_blank',
        },
      },
      text: [
        `By clicking Checkout, you accept the {clubTermsLink} and {mlbTermsOfUseLink}, as well as policies applicable to use.
        As part of your ticket purchase, you are required to acknowledge the ${this.clientName} Ticket {clubTermsLink} which includes,
        among other items, a release of liability and a mandatory arbitration agreement.`,

        `By selecting the Checkout button you agree to purchase the tickets selected, to accept any restrictions noted,
        and understand you are responsible for any payment needed to complete the purchase. Upon completing your purchase by selecting the Checkout button,
        you acknowledge and agree your credit card will be charged. All sales are final. All tickets are subject to the {clubTermsLink} and {mlbTermsOfUseLink}.`,

        `In connection with your purchase, you may receive customer service-related emails from the Chicago Cubs, including optional surveys regarding your gameday experience.
        You also may receive ticket-related commercial email from the Chicago Cubs. You may unsubscribe from ticket-related commercial emails at any time.`,
      ],
    };
  },
  defaultTooltipText: 'Review seats before continuing',
  teamId: '112',
  metaPixelAnalytics: true,
};
